import { Box, Collapse, Fade } from '@material-ui/core';
import { useLogout } from 'app/core/api/auth/api-auth';
import { contextActions } from 'app/core/redux/context-redux';
import styledConsts from 'app/styles/styledConsts';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import { TransientExcept } from 'app/tools/general.types';
import { LSKey } from 'app/tools/hooks/local-storage/local-storage.types';
import useLocalState from 'app/tools/hooks/local-storage/use-local-storage';
import { appScrollElement$ } from 'app/tools/hooks/misc-hooks';
import useSafeAreaInsets from 'app/tools/hooks/use-safe-area-insets';
import React from 'react';
import { isAndroid, isChrome } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components/macro';
import Icon from '../components/icon/Icon';
import IconLib from '../components/icon/icon-lib';
import { PadExpand } from '../components/misc';
import {
  useInnerFramePortal,
  useOuterFramePortal,
} from '../context-hooks/use-frame-portal.hook';
import { usePageTitle } from '../context-hooks/use-titlebar.hook';
import { ViewportProvider } from '../context-hooks/use-viewport.hook';
import SideMenu from '../page-parts/SideMenu';
import PushInFrom from './animation/PushInFrom';

const OuterAppFrame = styled.div`
  position: absolute;
  overflow: hidden;
  margin: auto;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  max-width: 500px;
  max-height: 1000px;

  ${styledMixins.fullsize}
`;

const outerPadAmount = css<ContentsOpts>`
  ${p =>
    p.$edgeToEdge
      ? 0
      : p.$soloContent
      ? styledFuncs.pad(3)
      : styledFuncs.pad()}
`;

type ContentsOpts = Partial<
  TransientExcept<InnerProps, 'children'> & {
    $centered: boolean;
  }
>;
export const FrameContentsInner = styled(Box)<ContentsOpts>`
  display: flex;
  flex-direction: column;
  align-items: ${p => (p.$centered ? 'center' : 'stretch')};
  justify-content: ${p => (p.$centered ? 'center' : 'flex-start')};
  width: 100%;
  flex: 1 1 100%;
  position: relative;

  padding: ${outerPadAmount};
  ${p => {
    const margin = p.$edgeToEdge ? '0px' : styledFuncs.pad()(p);
    return styledMixins.addMarginOnChildrenWithExceptions(margin);
  }}

  > * {
    ${p =>
      p.$centered &&
      css`
        text-align: center;
      `}
  }

  background: ${p =>
    p.theme.palette.background[p.$cardBgColor ? 'default' : 'paper']};
`;
const Contents = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
`;

const Header = styled(Box)`
  flex: 0 0 auto;
  z-index: 2;
`;
type OfflineTopBarOpts = {
  $online: boolean;
  $devTestingMode?: boolean;
  $topInset?: number;
};
const OfflineTopBar = styled.div<OfflineTopBarOpts>`
  ${styledMixins.flexCenter}
  flex-direction: row;
  padding: 2px 4px;
  padding-top: ${p => p.$topInset || 2}px;
  height: ${p => (p.$topInset || 2) + 20}px;
  color: ${p => (p.$devTestingMode ? 'inherit' : 'white')};
  background-color: ${p =>
    p.$online ? styledConsts.cPrimary : styledConsts.cError};
  ${p => p.$devTestingMode && styledMixins.stripeyWarnBackground}
  > *:first-child {
    font-weight: bold;
    font-size: 12px;
    margin-right: 0.5em;
  }
`;
const Footer = styled(Header)`
  box-shadow: 0px -2px 10px ${styledConsts.shadowColor};
  z-index: 2;
`;
const MenuContainer = styled(PushInFrom).attrs({ side: 'left' })`
  position: absolute;
  display: flex;
  flex-direction: column;
  z-index: 10;
  height: 100%;
  width: 80%;
  max-width: 250px;
  background: white;
`;
const MenuShadowOverlay = styled.div`
  position: absolute;
  z-index: 3;
  ${styledMixins.fullsize};
  background: rgba(0, 0, 0, 0.2);
`;

type InnerProps = {
  children: React.ReactNode;
  edgeToEdge?: boolean;
  soloContent?: boolean;
  centerContent?: boolean;
  cardBgColor?: boolean;
};

type Props = {
  header?: React.ReactNode;
  footer?: React.ReactNode;
} & InnerProps;

function ViewFrameInner({
  children,
  edgeToEdge = false,
  soloContent = false,
  centerContent = false,
  cardBgColor = false,
}: InnerProps): JSX.Element {
  return (
    <FrameContentsInner
      $soloContent={soloContent}
      $centerContent={centerContent}
      $centered={centerContent || soloContent}
      $edgeToEdge={edgeToEdge}
      $cardBgColor={cardBgColor}
    >
      {soloContent ? (
        <>
          {/* NOTE: these PadExpands shift solo-content upwards, and
          have the 'secondary' prop so that they can be override by
          PadExpand components inside the View component */}
          <PadExpand proportion={1} secondary />
          {children}
          <PadExpand proportion={2} secondary />
        </>
      ) : (
        children
      )}
    </FrameContentsInner>
  );
}

const StyledCollapse = styled(Collapse)`
  flex-shrink: 0;
`;
const NetworkIndicator: React.VFC<{ topInset?: number }> = ({
  topInset,
}) => {
  const online = useSelector(s => s.context.appOnline);
  const { request$: logout$ } = useLogout();
  const showIndicator = !online || online === 'backOnline';
  let message = online ? 'Back online!' : 'No internet connection';
  const isAndroidChrome = isAndroid && isChrome;
  // NOTE: Chrome on android has it's own 'no connection' message.

  const [devTestingMode, setDevTestingMode] = useLocalState<boolean>(
    LSKey.DEV_TESTING_MODE,
    false
  );
  if (devTestingMode) {
    message = showIndicator ? message : '';
    const msgII = message ? `| ${message}` : 'active! (tap to exit)';

    message = `Dev-Mode ${msgII}`;
  }

  return (
    <StyledCollapse
      in={(showIndicator && !isAndroidChrome) || devTestingMode}
    >
      <OfflineTopBar
        $online={!!online}
        $devTestingMode={devTestingMode}
        $topInset={topInset}
        onClick={() => {
          if (devTestingMode) {
            setDevTestingMode(false);
            logout$().subscribe();
          }
        }}
      >
        {!online && <Icon inline icon={IconLib.ERROR} />}
        {message}
      </OfflineTopBar>
    </StyledCollapse>
  );
};

export default function ViewFrame({
  header,
  footer,
  ...props
}: Props): JSX.Element {
  const dispatch = useDispatch();
  const drawerOpen = useSelector(s => s.context.menuOpen);
  const { scrollHeader } = usePageTitle();
  const { top } = useSafeAreaInsets();
  const [, setInnerFrameRef] = useInnerFramePortal();
  const [, setOuterFrameRef] = useOuterFramePortal();

  return (
    <OuterAppFrame ref={ref => setOuterFrameRef(ref)}>
      <ViewportProvider rootProvider>
        {!scrollHeader && (
          <>
            <Header>{header}</Header>
          </>
        )}
        <NetworkIndicator
          topInset={!header || scrollHeader ? top : undefined}
        />

        <ViewportProvider onlyUpdateHeight>
          <Contents
            ref={ref => {
              if (ref) appScrollElement$.next(ref);
              setInnerFrameRef(ref);
            }}
          >
            {scrollHeader && <Header>{header}</Header>}
            <ViewportProvider onlyUpdateWidth>
              <ViewFrameInner {...props} />
            </ViewportProvider>
          </Contents>
        </ViewportProvider>

        <Footer>{footer}</Footer>

        <Fade in={drawerOpen} mountOnEnter unmountOnExit>
          <MenuShadowOverlay
            onClick={() =>
              dispatch(contextActions.menuOpen.set(false))
            }
          />
        </Fade>
        <MenuContainer in={drawerOpen}>
          <SideMenu />
        </MenuContainer>
      </ViewportProvider>
    </OuterAppFrame>
  );
}
